import { useRef, useEffect, useState } from 'react';
import Webcam from 'react-webcam';

import { WarningMessage } from '@common/components';
import { useMediaTest } from '../../../hooks/useMediaTest';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../dialog';
import { Button } from '../../shadcn/button';
import { VideoTest } from './VideoTest';
import { AudioTest } from './AudioTest';
import { translations } from '../../../translations/chatInputTranslations';

interface MediaTestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  testPermissions: { video?: boolean; audio?: boolean };
  themeData?: any;
  languageCode?: string;
  isTextRightToLeft: boolean;
}

export function MediaTestDialog({
  isOpen,
  onClose,
  onContinue,
  testPermissions,
  themeData,
  languageCode = 'en',
  isTextRightToLeft = false,
}: MediaTestDialogProps) {
  const webcamRef = useRef<Webcam>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Get translations for current language
  const t = translations[languageCode] || translations.en;

  // Use the enhanced media test hook
  const {
    // State
    isFrontCamera,
    isStreamLoading,
    isSwitchingCamera,
    hasStartedTest,
    error,
    browserWarning,
    audioLevel,
    canSwitchCamera,
    isVideoReady,
    isAudioReady,

    // Actions
    openMediaTest,
    startMediaTest,
    stopMediaTest,
    handleSwitchCamera,

    // Webcam integration
    getVideoConstraints,
    onUserMediaError,
    onUserMedia,
  } = useMediaTest({ onTestComplete: onContinue });

  // Set test permissions when dialog opens
  useEffect(() => {
    if (isOpen && (testPermissions.video || testPermissions.audio)) {
      openMediaTest(testPermissions);
    }
  }, [isOpen, testPermissions, openMediaTest]);

  // Start media test when modal opens
  useEffect(() => {
    if (isOpen && !hasStartedTest && (testPermissions.video || testPermissions.audio)) {
      startMediaTest(testPermissions);
    }
  }, [isOpen, hasStartedTest, testPermissions, startMediaTest]);

  // Cleanup on unmount or when dialog closes
  useEffect(() => {
    if (!isOpen) {
      stopMediaTest();
    }
    return () => {
      stopMediaTest();
    };
  }, [isOpen, stopMediaTest]);

  const handleClose = () => {
    stopMediaTest();
    onClose();
  };

  const handleContinue = () => {
    stopMediaTest();
    onContinue();
  };

  const isContinueButtonEnabled =
    (!testPermissions.video || isVideoReady) && (!testPermissions.audio || isAudioReady);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md max-h-[100vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        dir="auto"
      >
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 mb-2 ${isTextRightToLeft ? 'mt-3' : ''}`}
          >
            {t.quickSetupCheck}
          </DialogTitle>
          <DialogDescription className={`${isTextRightToLeft ? 'text-right' : 'text-left'}`}>
            {t.testYour}{' '}
            {testPermissions.video && testPermissions.audio
              ? t.cameraAndMicrophone.toLocaleLowerCase()
              : testPermissions.video
                ? t.camera.toLowerCase()
                : t.microphone.toLowerCase()}{' '}
            {t.ensureWorkingProperly}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

          {browserWarning && (
            <WarningMessage>
              <div>
                <p className="mb-1">{browserWarning}</p>
                {linkCopied ? (
                  <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                    {t.linkCopied}
                  </span>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setLinkCopied(true);
                        setTimeout(() => setLinkCopied(false), 2000);
                      }}
                      className="underline hover:no-underline cursor-pointer font-medium text-amber-700 bg-transparent border-none p-0"
                    >
                      {t.copyThisLink}
                    </button>{' '}
                    {t.openInYourBrowser}
                  </>
                )}
              </div>
            </WarningMessage>
          )}

          {hasStartedTest && (
            <div className="space-y-4 pt-2">
              {/* Video Test */}
              {testPermissions.video && (
                <VideoTest
                  isVideoReady={isVideoReady}
                  canSwitchCamera={canSwitchCamera}
                  handleSwitchCamera={handleSwitchCamera}
                  isSwitchingCamera={isSwitchingCamera}
                  webcamRef={webcamRef}
                  isFrontCamera={isFrontCamera}
                  isStreamLoading={isStreamLoading}
                  getVideoConstraints={getVideoConstraints}
                  onUserMediaError={onUserMediaError}
                  onUserMedia={onUserMedia}
                  languageCode={languageCode}
                  isTextRightToLeft={isTextRightToLeft}
                />
              )}
              {/* Audio Test */}
              {testPermissions.audio && (
                <AudioTest
                  isTextRightToLeft={isTextRightToLeft}
                  isAudioReady={isAudioReady}
                  audioLevel={audioLevel}
                  languageCode={languageCode}
                />
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
            {t.skip}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!isContinueButtonEnabled}
            style={{ backgroundColor: themeData?.primary || '#096846' }}
            className="w-full sm:w-auto"
          >
            {t.continueButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
